import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import SearchIcon from '@mui/icons-material/Search';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import logo from '../assets/logo.png';

const navLinks = [
  { label: 'Home', to: '/' },
  { label: 'Store', to: '/store' },
  { label: 'About', to: '/AboutUs' },
  { label: 'Contact', to: '/Contact' },
];

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setIsMenuOpen(false);
    setSearchOpen(false);
    setSearchQuery('');
  }, [location.pathname, location.search]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/store?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false);
      setSearchQuery('');
    }
  };

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  const isActive = (to) => {
    const path = location.pathname.toLowerCase();
    const target = to.toLowerCase();
    if (target === '/') return path === '/';
    return path === target;
  };

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 h-12 bg-white/90 backdrop-blur-md border-b border-gray-200">
        <div className="page-wrap h-full flex items-center justify-between gap-3">
          <Link to="/" className="flex-shrink-0 flex items-center" aria-label="Apertscribe home">
            <img src={logo} alt="Apertscribe" className="h-8 w-auto object-contain" />
          </Link>

          <ul className="hidden md:flex flex-1 items-center justify-center gap-5 lg:gap-7">
            {navLinks.map((link) => (
              <li key={link.to}>
                <Link
                  to={link.to}
                  className={`text-sm tracking-wide transition-colors ${
                    isActive(link.to)
                      ? 'font-bold text-black'
                      : 'font-normal text-black/70 hover:text-black'
                  }`}
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>

          <div className="flex items-center gap-3 flex-shrink-0">
            <button
              type="button"
              className="text-black hover:text-black/70 transition-colors"
              onClick={() => setSearchOpen((o) => !o)}
              aria-label="Search"
            >
              <SearchIcon style={{ fontSize: 22 }} />
            </button>
            <Link
              to="/cart"
              className="text-black hover:text-black/70 transition-colors"
              aria-label="Cart"
            >
              <ShoppingBagOutlinedIcon style={{ fontSize: 22 }} />
            </Link>
            <button
              type="button"
              className="md:hidden text-black"
              onClick={() => setIsMenuOpen(true)}
              aria-label="Open menu"
            >
              <MenuIcon style={{ fontSize: 26 }} />
            </button>
          </div>
        </div>

        {/* Search bar dropdown */}
        {searchOpen && (
          <div className="border-t border-gray-200 bg-white">
            <form onSubmit={handleSearch} className="page-wrap py-3 flex gap-2">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search products..."
                autoFocus
                className="flex-1 border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-[#BB0000]"
              />
              <button
                type="submit"
                className="bg-[#BB0000] text-white text-sm font-bold px-5 py-2 rounded-lg hover:bg-[#990000] transition-colors"
              >
                Search
              </button>
            </form>
          </div>
        )}
      </nav>

      <div className="h-12" aria-hidden="true" />

      {isMenuOpen && (
        <div className="fixed inset-0 z-[60] bg-white md:hidden flex flex-col">
          <div className="h-12 px-4 flex items-center justify-between border-b border-gray-200">
            <Link to="/" onClick={() => setIsMenuOpen(false)}>
              <img src={logo} alt="Apertscribe" className="h-8 w-auto object-contain" />
            </Link>
            <button
              type="button"
              className="text-black"
              onClick={() => setIsMenuOpen(false)}
              aria-label="Close menu"
            >
              <CloseIcon style={{ fontSize: 24 }} />
            </button>
          </div>
          <ul className="flex-1 overflow-y-auto px-6 py-8 flex flex-col gap-5">
            {navLinks.map((link) => (
              <li key={link.to}>
                <Link
                  to={link.to}
                  className={`text-[28px] tracking-tight ${
                    isActive(link.to) ? 'font-bold text-black' : 'font-normal text-black/80'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {link.label}
                </Link>
              </li>
            ))}
            <li className="pt-4 border-t border-gray-200">
              <Link
                to="/cart"
                className="text-[28px] font-bold text-black tracking-tight"
                onClick={() => setIsMenuOpen(false)}
              >
                Cart
              </Link>
            </li>
          </ul>
        </div>
      )}
    </>
  );
};

export default Navbar;
